import { Avatar, Divider, Paper, Stack, Typography } from '@mui/material';
import { IEntitlement } from 'API/compassRights';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary, useIsDesktop } from 'Hooks';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { dotDateFormat } from 'Config';
import {
  ArrowBackBlue,
  PurpleOutlineWarningIcon,
  UploadPrescription,
} from 'Images';
import { OneOrTwoButtons } from 'Components/OneOrTwoButtons';

interface IEntitlementItem {
  entitlement: IEntitlement;
}

export const Entitlement = ({ entitlement }: IEntitlementItem) => {
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();

  const dictionary = useDictionary(
    Dictionary,
    DICTIONARY.CompassRights
  ).entitlements;

  return (
    <Paper
      sx={{
        border: '1px solid primary.shade_100',
        borderRadius: '16px',
        boxShadow: isDesktop
          ? '0px 2px 24px 0px #5B86EF1F'
          : '0px 4px 12px 0px #081E5412',
        background: '#FFFFFF',
        width: '100%',
        display: 'flex',
        flexFlow: isDesktop ? 'row' : 'column',
        gap: '15px',
      }}
    >
      <Stack
        gap='32px'
        bgcolor='primary.shade_50'
        width={isDesktop ? '43%' : undefined}
        p='24px'
        border='1px solid primary.shade_100'
        sx={{ borderTopLeftRadius: '16px', borderTopRightRadius: '16px' }}
      >
        <Typography variant='body1' gap='32px' component='h2'>
          {entitlement?.name}
        </Typography>
        {(!!entitlement?.lastDateTimeUsed ||
          entitlement?.automatic ||
          entitlement?.oneTime) && (
          <Stack flexDirection={isDesktop ? 'column' : 'row'} gap='20px'>
            {!!entitlement?.lastDateTimeUsed && (
              <Stack gap='8px'>
                <Typography variant='body6'>
                  {dictionary.lastUsageDate}
                </Typography>
                <Typography variant='body2'>
                  {format(
                    new Date(entitlement.lastDateTimeUsed),
                    dotDateFormat
                  )}
                </Typography>
              </Stack>
            )}
            {(entitlement?.automatic || entitlement?.oneTime) && (
              <Stack gap='8px'>
                <Typography variant='body6'>{dictionary.type}</Typography>
                <Typography
                  sx={{
                    bgcolor: entitlement.automatic ? '#E0F6EF' : '#F4E4FF',
                    border: `1px solid ${
                      entitlement.automatic ? '#B8EAD9' : '#E4C1FF'
                    }`,
                    borderRadius: '6px',
                    padding: '2px 12px 4px',
                    width: 'fit-content',
                  }}
                  variant='body2'
                >
                  {entitlement.automatic
                    ? dictionary.automatic
                    : dictionary.singleUse}
                </Typography>
              </Stack>
            )}
          </Stack>
        )}
      </Stack>
      <Stack gap='24px' p={'24px 32px 24px 24px'}>
        <Typography variant='body6'>{entitlement?.description}</Typography>
        {entitlement?.contact && (
          <Typography variant='body5'>
            {dictionary.contact(entitlement.contact)}
          </Typography>
        )}
        {entitlement?.eligibleFrequency && (
          <Stack gap='6px' alignItems='center' flexDirection='row'>
            <Avatar
              alt=''
              src={PurpleOutlineWarningIcon}
              sx={{ width: '20px', height: '20px' }}
            />
            <Typography variant='body6'>
              {`${dictionary.nextDateUse} ${entitlement.eligibleFrequency}`}
            </Typography>
          </Stack>
        )}
        {isDesktop &&
          (!!entitlement?.additionalInfoText ||
            !!entitlement?.actionButtonText) && (
            <Divider sx={{ borderColor: 'primary.shade_100' }} />
          )}
        <Stack
          sx={
            isDesktop
              ? undefined
              : {
                  mb: '-24px',
                  ml: '-24px',
                  width: 'calc(100% + 56px)',
                }
          }
        >
          <OneOrTwoButtons
            rightButton={
              !!entitlement?.additionalInfoText
                ? {
                    label: `${dictionary.extraInfo} ${
                      entitlement?.additionalInfoText || ''
                    }`,
                    icon: UploadPrescription,
                    onClick: () => {
                      if (!!entitlement?.additionalInfoLink)
                        window.open(entitlement.additionalInfoLink, '_blank');
                    },
                  }
                : undefined
            }
            leftButton={
              !!entitlement?.actionButtonText
                ? {
                    label: entitlement.actionButtonText || '',
                    icon: ArrowBackBlue,
                    onClick: () => {
                      if (entitlement.actionButtonLink)
                        navigate(entitlement.actionButtonLink);
                    },
                  }
                : undefined
            }
            width={isDesktop ? 'fit-content' : '100%'}
            cardVariant={!isDesktop}
          />
        </Stack>
      </Stack>
    </Paper>
  );
};
